/**
 * SpecialsTab - Today's Specials management component
 * Extracted from AdminPage.jsx
 */
import { useState, useEffect } from 'react';
import { Search, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/utils/apiClient';
import { categoryLabels } from '@/components/admin/shared/constants';

export default function SpecialsTab({ menuItems }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setSettings(res.data);
      setSelectedItems(res.data?.daily_specials?.item_ids || []);
    } catch (err) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updatedSettings = {
        ...settings,
        daily_specials: {
          ...settings?.daily_specials,
          item_ids: selectedItems
        }
      };
      
      const res = await api.put('/admin/settings', updatedSettings);
      setSettings(res.data.settings);
      toast.success('Specials updated successfully!');
    } catch (err) {
      toast.error('Failed to save specials');
    }
    setSaving(false);
  };

  const updateSpecialsSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      daily_specials: {
        ...prev?.daily_specials,
        [key]: value
      }
    }));
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const filteredMenuItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory && item.is_available;
  });

  const selectedItemsDetails = menuItems.filter(item => selectedItems.includes(item.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="specials-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-foreground">Today's Specials</h1>
          <p className="text-muted-foreground mt-1">Manage the rotating specials banner on your homepage</p>
        </div>
        <Button 
          onClick={saveSettings}
          disabled={saving}
          className="rounded-full bg-primary hover:bg-primary/90"
          data-testid="save-specials-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Settings Panel */}
        <div className="lg:col-span-1 space-y-6">
          {/* Enable Toggle */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
            <h2 className="font-heading text-xl mb-4 text-foreground">Display Settings</h2>
            
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.daily_specials?.enabled !== false}
                  onChange={(e) => updateSpecialsSetting('enabled', e.target.checked)}
                  className="rounded"
                  data-testid="specials-enabled-toggle"
                />
                <div>
                  <p className="font-medium text-foreground">Enable Specials Banner</p>
                  <p className="text-xs text-muted-foreground">Show on homepage</p>
                </div>
              </label>

              <div className="space-y-2">
                <Label className="text-foreground">Banner Title</Label>
                <Input
                  value={settings?.daily_specials?.title || "Today's Specials"}
                  onChange={(e) => updateSpecialsSetting('title', e.target.value)}
                  placeholder="Today's Specials"
                  data-testid="specials-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Subtitle</Label>
                <Input
                  value={settings?.daily_specials?.subtitle || ''}
                  onChange={(e) => updateSpecialsSetting('subtitle', e.target.value)}
                  placeholder="Fresh picks rotating throughout the day"
                  data-testid="specials-subtitle-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Rotation Speed (seconds)</Label>
                <Input
                  type="number"
                  min="2"
                  max="30"
                  value={settings?.daily_specials?.rotation_seconds || 5}
                  onChange={(e) => updateSpecialsSetting('rotation_seconds', parseInt(e.target.value))}
                  data-testid="specials-rotation-input"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.daily_specials?.show_prices !== false}
                  onChange={(e) => updateSpecialsSetting('show_prices', e.target.checked)}
                  className="rounded"
                  data-testid="specials-show-prices-toggle"
                />
                <span className="text-foreground">Show prices</span>
              </label>

              <div className="space-y-2">
                <Label className="text-foreground">Background Style</Label>
                <Select 
                  value={settings?.daily_specials?.background_style || 'gradient'} 
                  onValueChange={(value) => updateSpecialsSetting('background_style', value)}
                >
                  <SelectTrigger data-testid="specials-bg-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gradient">Gradient</SelectItem>
                    <SelectItem value="solid">Solid Color</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Selected Items Preview */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
            <h2 className="font-heading text-xl mb-4 text-foreground">
              Selected Items ({selectedItems.length})
            </h2>
            
            {selectedItemsDetails.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No items selected. Choose items from the menu on the right.
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedItemsDetails.map((item, index) => (
                  <div 
                    key={item.id}
                    className="flex items-center gap-3 p-3 bg-muted rounded-xl"
                    data-testid={`selected-special-${item.id}`}
                  >
                    <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                      {index + 1}
                    </span>
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">${item.price?.toFixed(2)}</p>
                    </div>
                    <button
                      onClick={() => toggleItemSelection(item.id)}
                      className="p-1 hover:bg-red-100 rounded-full text-red-500"
                      data-testid={`remove-special-${item.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Menu Items Selection */}
        <div className="lg:col-span-2 bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-4 text-foreground">Select Menu Items</h2>
          
          {/* Search & Filter */}
          <div className="flex gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="specials-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="specials-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Items Grid */}
          <div className="grid md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
            {filteredMenuItems.map(item => {
              const isSelected = selectedItems.includes(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => toggleItemSelection(item.id)}
                  className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-transparent bg-muted hover:border-primary/30'
                  }`}
                  data-testid={`menu-item-select-${item.id}`}
                >
                  <div className="relative">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary/80 rounded-xl flex items-center justify-center">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{item.name}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-primary font-medium">${item.price?.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground capitalize px-2 py-0.5 bg-muted rounded-full">
                        {categoryLabels[item.category] || item.category}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredMenuItems.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No items found matching your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
